import { useEffect, useCallback, useRef } from 'react'
import { Icon } from '@iconify/react'
import { useContainerTabsContext } from './ContainerTabsContext'
import type { ContainerTabProps } from './types'
import styles from './ContainerTabs.module.css'

/**
 * Botão individual de aba dentro de container.
 * Registra-se automaticamente no contexto quando montado.
 */
export function ContainerTab({
  children,
  value,
  icon,
  disabled = false,
  className,
}: ContainerTabProps) {
  const { activeTab, setActiveTab, tabsId, registerTab, unregisterTab } = useContainerTabsContext()
  const buttonRef = useRef<HTMLButtonElement>(null)
  
  // Registra a aba quando montada
  useEffect(() => {
    registerTab({
      value,
      label: typeof children === 'string' ? children : value,
      icon,
      disabled,
    })
    
    return () => {
      unregisterTab(value)
    }
  }, [value, children, icon, disabled, registerTab, unregisterTab])
  
  const isActive = activeTab === value
  
  const handleClick = useCallback(() => {
    if (!disabled) {
      setActiveTab(value)
    }
  }, [disabled, setActiveTab, value])
  
  // Navegação por teclado
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return
      
      const tabs = buttonRef.current?.closest('[role="tablist"]')?.querySelectorAll('[role="tab"]')
      if (!tabs) return
      
      const tabsArray = Array.from(tabs) as HTMLButtonElement[]
      const currentIndex = tabsArray.findIndex((tab) => tab === buttonRef.current)
      
      let nextIndex: number | null = null
      
      switch (e.key) {
        case 'ArrowLeft':
          nextIndex = currentIndex > 0 ? currentIndex - 1 : tabsArray.length - 1
          break
        case 'ArrowRight':
          nextIndex = currentIndex < tabsArray.length - 1 ? currentIndex + 1 : 0
          break
        case 'Home':
          nextIndex = 0
          break
        case 'End':
          nextIndex = tabsArray.length - 1
          break
        default:
          return
      }
      
      e.preventDefault()
      const nextTab = tabsArray[nextIndex]
      nextTab?.focus()
      nextTab?.click()
    },
    [disabled]
  )
  
  const tabId = `${tabsId}-tab-${value}`
  const panelId = `${tabsId}-panel-${value}`
  
  return (
    <button
      ref={buttonRef}
      type="button"
      id={tabId}
      role="tab"
      aria-selected={isActive}
      aria-controls={panelId}
      aria-disabled={disabled}
      tabIndex={isActive ? 0 : -1}
      data-tab-value={value}
      disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={[
        styles.containerTab,
        isActive && styles.containerTabActive,
        disabled && styles.containerTabDisabled,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {icon && <Icon icon={icon} className={styles.containerTabIcon} />}
      <span className={styles.containerTabLabel}>{children}</span>
    </button>
  )
}
